import { Appointment, AppointmentChecklist } from "./admissions.model";
import { NeurovascularAssessment } from "./neurovascular-assessment.model";
import { Patient } from "./patient.model";
import { ProcedureRoomListing } from "./procedure-room.model";
import {
  BloodGlucose,
  ChiefComplaint,
  DispenseSupply,
  EncounterSchedule,
  LeadEKG,
  Medication,
  ModifiedAldreteScoring,
  Procedure,
  SnacksAndMeals,
  Vitals,
} from "./procedure.model";
import { PatientDoc } from "./patient-docs.model";
import { PriorAuth } from "./prior-auth.model";
import { Coverage } from "./coverages.model";
import {
  ChecklistQuestionsCache,
  HpiQuestions,
  Provider,
} from "./lookup.model";
import { ReferralChecklist } from "./referral-checklist.model";
import { OldLabInfo } from "./old-labs.model";
import { ActivityNote } from "./activity-notes.model";
import { PreProcedureAssessment } from "./pre-procedure-assessment";
import { Allergy } from "./allergy.model";
import { AdministerMedication } from "./administer-medication.model";
import { AccessSite } from "./access-site.model";
import { PhiProcedureMedicationRow } from "./phi-medications-reconciliation.model";
import { Act } from "./act.model";
import { ProcedureSummary } from "./procedure-summary.model";
import { ProcedureTimeout } from "./procedure-timeout.model";
import { CptCodesForEncounter, ICDCodesForEncounter } from "./cpt-codes.model";
import { Discharge } from "./discharge.model";
import {
  EncounterFamilyHistory,
  EncounterMedicalHistory,
  EncounterMedication,
  EncounterSocialHistory,
  EncounterSurgicalHistory,
  Problem,
  ProblemService,
  SoapNote,
} from "./problems.model";
import { Context } from "../models/contexts.model"
import { Task } from "../models/tasks.model"
import { HpiFindings, HpiSummary } from "./hpi-findings";
import { RosSummary } from "./ros-summary";

export class ApplicationCache {
  patient: Patient;
  appointment: Appointment;
  physician: Provider;

  documents: PatientDoc[] = [];
  schedule: EncounterSchedule;
  procedure: Procedure;
  priorAuth: PriorAuth;
  priorAuths: PriorAuth[] = [];
  coverages: Coverage[] = [];

  checklistFlags: AppointmentChecklist;
  checklistQuestions: ChecklistQuestionsCache[] = [];
  hpiQuestions: HpiQuestions[] = [];
  hpiSymptoms: any[] = [];
  rosQuestions: HpiQuestions[] = [];
  rosSymptoms: any[] = [];
  
  triageQuestions: Record<string, {
    id: string;
    question: string;
    comment: string;
    isSelected: 'Positive' | 'Negative' | 'None';
  }[]> = {};

  checklistValues: ReferralChecklist;
  labs: OldLabInfo[] = [];

  chiefComplaint: ChiefComplaint;
  followUpChiefComplaint: ChiefComplaint;
  allergies: Allergy[] = [];
  preAnesthsiaSummary: HpiSummary;
  hpiSummary: HpiFindings;
  rosSummary: RosSummary;

  // procedure related objects
  procedureListing: ProcedureRoomListing;
  neurovascularAssessment: NeurovascularAssessment[] = [];
  snacksAndMeals: SnacksAndMeals[] = [];
  dispenseSupplies: DispenseSupply[] = [];
  vitals: Vitals[] = [];
  bloodGlucose: BloodGlucose[] = [];
  modifiedAldreteScoring: ModifiedAldreteScoring[] = [];
  leadEKG: LeadEKG[] = [];
  medicationReconciliation: Medication[] = [];
  /** POST-procedure PHI medication list (for checklist Complete Rec rule). */
  procedurePhiMedications: PhiProcedureMedicationRow[] = [];
  preProcedureAssessment: PreProcedureAssessment;
  /** Manual nurse notes from the procedure room Nurse Notes panel. */
  procedureRoomNurseActivityNotes: ActivityNote[] = [];
  /** Automated procedure-room activity rows (check-in, prior auth, assessments, etc.). */
  procedureRoomActivityNotes: ActivityNote[] = [];
  administerMeds: AdministerMedication[] = [];
  accessSites: AccessSite[] = [];
  acts: Act[] = [];
  procedureSummary: ProcedureSummary;
  procedureTimeout: ProcedureTimeout;
  cptCodes: CptCodesForEncounter[] = [];
  icdCodes: ICDCodesForEncounter[] = [];
  discharge: Discharge;

  /** Engage lead selected without clinic patientId — Global Notes uses this for referral-scoped activity/SMS. */
  engageNotesReferralId?: string | null;

  /** Engage Patients: patient name shown in Activity sidebar header (from list/details until cache patient resolves). */
  engageProspectPatientDisplayName?: string | null;
  /**
   * Engage referral `assigned_to` user id when set; null/empty means unassigned ("Assign to" / everyone).
   * Used to disable SMS "Join conversation" when the lead is not assigned to a specific user.
   */
  engageReferralAssignedToUserId?: string | null;

  // clinic v3 cache
  encounterProblems: Problem[] = [];
  encounterServices: ProblemService[] = [];
  encounterMedications: EncounterMedication[] = [];
  /** Plan clinical orders (lab, radiology, etc.) for note generation. */
  encounterClinicalOrders: unknown[] = [];
  encounterSoapNote: SoapNote;
  encounterContexts: Context[] = [];
  /** Contexts with `ownerType=procedure` for the procedure room workflow (separate from SOAP `encounterContexts`). */
  procedureRoomContexts: Context[] = [];
  tasks: Task[] = [];
  encounterSocialHistory: EncounterSocialHistory[] = [];
  encounterFamilyHistory: EncounterFamilyHistory[] = [];
  encounterMedicalHistory: EncounterMedicalHistory[] = [];
  encounterSurgicalHistory: EncounterSurgicalHistory[] = [];
  patientEncounters: any[] = [];
  recentAppointments: any[] = [];
  
  // AI-generated suggested services cache
  suggestedServices: any[] = [];
  
  // AI-generated suggested problems cache
  suggestedProblems: any[] = [];
  
  // Question caches
  pqrstaQuestions: any[] = [];
  opqrstQuestions: any[] = [];
  historyQuestions: any[] = [];
  
  // Section additional comments for history triage
  sectionAdditionalComments: Record<string, string> = {};
  
  // Templates cache
  templates: any[] = [];
  
  // Prompts cache
  prompts: any[] = [];
}
